import { Address } from 'types';
import { AddressValidationOutput } from 'graphql/generated/userApi';
import { Button } from 'primereact/button';
import { Checkbox } from 'primereact/checkbox';
import { Dialog } from 'primereact/dialog';
import { Messages } from 'primereact/messages';
import { ReactElement, useEffect, useRef, useState } from 'react';
import ReactGA from 'react-ga4';
import { showErrorMessage } from 'utils/errorHelpers';
import { useCartContext } from 'CartContext';
import { useForm } from 'react-hook-form';
import { useSetShippingAddress } from 'hooks/magentoHooks';
import { useValidateAddress } from 'hooks/userHooks';
import AddressFormInput from 'components/forms/AddressFormInput';
import FormInput from 'components/forms/FormInput';

function addressLabel(address?: Address | AddressValidationOutput) {
  if (!address) {
    return null;
  }
  const anyAddress: any = address;

  let zip = anyAddress.postalCode || anyAddress.zip;
  if (anyAddress.zip && anyAddress.zip4) {
    zip = `${anyAddress.zip}-${anyAddress.zip4}`;
  }
  const state = anyAddress.state || anyAddress.stateRegion.code;
  return (
    <>
      {address.address1}
      {address.address2 && (
        <>
          <br />
          {address.address2}
        </>
      )}
      <br />
      {`${address.city}, ${state} ${zip}`}
    </>
  );
}

function addressesMatch(
  validatedAddress: AddressValidationOutput,
  address: Address,
): boolean {
  return (
    validatedAddress.address1 === address.address1.toUpperCase() &&
    (validatedAddress.address2 || '') ===
      (address.address2 || '').toUpperCase() &&
    validatedAddress.city === address.city.toUpperCase() &&
    validatedAddress.state === address.stateRegion.code.toUpperCase() &&
    `${validatedAddress.zip}-${validatedAddress.zip4}` === address.postalCode
  );
}

export default function ShippingForm(): ReactElement {
  const { billingAddress, shippingAddress, userInfo, setShippingAddress } =
    useCartContext();

  const [useBillingAddress, setUseBillingAddress] = useState(true);
  const [shippingAddressLocal, setShippingAddressLocal] = useState<
    Address | undefined
  >();
  const [addressMutationState, setMagentoShippingAddress] =
    useSetShippingAddress();
  const messages = useRef<Messages>(null);
  const [addressApproved, setAddressApproved] = useState<boolean>(false);
  const [validate, setValidate] = useState(false);
  const [validateDialogVisable, setValidateDialogVisible] = useState(false);
  const validateAddressState = useValidateAddress(shippingAddressLocal);

  const defaultValues: Address = {
    firstName: shippingAddress?.firstName || '',
    lastName: shippingAddress?.lastName || '',
    address1: shippingAddress?.address1 || '',
    address2: shippingAddress?.address2 || '',
    city: shippingAddress?.city || '',
    postalCode: shippingAddress?.postalCode || '',
    stateRegion: shippingAddress?.stateRegion || { name: '', code: '' },
    country: { name: 'United States', iso3: 'USA', code: 'US' },
    mobile: userInfo?.mobile || shippingAddress?.mobile || '',
  };

  const { control, formState, handleSubmit, reset, setError } =
    useForm<Address>({
      defaultValues,
    });

  const { errors } = formState;

  useEffect(() => {
    if (useBillingAddress) {
      reset(billingAddress);
    } else {
      reset(defaultValues);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [useBillingAddress]);

  useEffect(() => {
    if (
      addressMutationState.data &&
      !addressMutationState.error &&
      shippingAddressLocal
    ) {
      setShippingAddress(shippingAddressLocal);
    }
    if (addressMutationState.error) {
      showErrorMessage(messages.current, addressMutationState.error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addressMutationState, shippingAddressLocal]);

  useEffect(() => {
    if (addressApproved && shippingAddressLocal) {
      setMagentoShippingAddress(shippingAddressLocal);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addressApproved, shippingAddressLocal]);

  useEffect(() => {
    const { validatedAddress, fetching, error } = validateAddressState;
    if (validate && error && !fetching) {
      setValidateDialogVisible(true);
      setValidate(false);
    } else if (
      validate &&
      shippingAddressLocal &&
      validatedAddress?.zip &&
      !fetching
    ) {
      if (addressesMatch(validatedAddress, shippingAddressLocal)) {
        handleApprove();
      } else {
        setValidateDialogVisible(true);
      }
      setValidate(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [validate, validateAddressState, shippingAddressLocal]);

  const handleApprove = () => {
    const { validatedAddress, fetching } = validateAddressState;
    if (validatedAddress?.zip && !fetching) {
      setShippingAddressLocal(prev => {
        if (prev) {
          return {
            ...prev,
            city: validatedAddress.city,
            address1: validatedAddress.address1,
            address2: validatedAddress.address2,
            stateRegion: {
              name: shippingAddress?.stateRegion.name || '',
              code: validatedAddress.state,
            },
            postalCode: `${validatedAddress.zip}-${validatedAddress.zip4}`,
          };
        }
        return prev;
      });
      setAddressApproved(true);
      setValidateDialogVisible(false);
    }
  };

  const onSubmit = (data: Address) => {
    if (!data.country?.code || !data.stateRegion?.code) {
      if (!data.country?.code) {
        setError('country', {
          type: 'required',
          message: 'Country is required.',
        });
      }
      if (!data.stateRegion?.code) {
        setError('stateRegion', {
          type: 'required',
          message: 'State is required.',
        });
      }
      return;
    }
    setShippingAddressLocal(data);
    setValidate(true);
    ReactGA.event({
      category: 'ecommerce',
      action: 'checkout_step_3',
      label: 'Checkout - Address Successful',
    });
  };

  const submitting =
    addressMutationState.fetching || validateAddressState.fetching;

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="p-fluid">
      <div className="grid">
        <div className="col-12">
          <div className="my-2 flex flex-row align-items-center">
            <Checkbox
              inputId="useBillingAddress"
              value="useBillingAddress"
              onChange={e => setUseBillingAddress(e.checked)}
              checked={useBillingAddress}
            />
            <label
              htmlFor="useBillingAddress"
              className="p-checkbox-label my-3"
              style={{ fontWeight: 'bold' }}
            >
              Use billing address
            </label>
          </div>
          <h2 className="form-heading">Your Shipping Address</h2>
          <div className="grid">
            <div className="col-12 md:col-6">
              <FormInput
                fieldName="firstName"
                label="First Name*"
                rules={{ required: 'First name is required.' }}
                errors={errors}
                control={control}
              />
            </div>
            <div className="col-12 md:col-6">
              <FormInput
                fieldName="lastName"
                label="Last Name*"
                rules={{ required: 'Last name is required.' }}
                errors={errors}
                control={control}
              />
            </div>
          </div>
          <AddressFormInput control={control} errors={errors} />
          <Messages ref={messages} />
          <Button
            type="submit"
            label="Continue"
            className="uppercase border-noround c-button-primary right-auto"
            loading={submitting}
            id="checkout-address"
          />
        </div>
      </div>
      <Dialog
        visible={validateDialogVisable}
        onHide={() => {
          setValidate(false);
          setValidateDialogVisible(false);
        }}
        breakpoints={{ '1200px': '50vw', '960px': '75vw', '640px': '90vw' }}
        style={{ width: '33vw' }}
        className="address-validation"
        header="Address Validation"
      >
        <>
          <div className="px-4">
            {!!validateAddressState?.error && (
              <p className="mt-0 mb-4">{`${validateAddressState.error} The address needs to be updated.`}</p>
            )}
            <div className="grid">
              <div className="col-12 sm:col-6">
                <p className="font-bold block mb-3">You Entered:</p>
                <p>{addressLabel(shippingAddressLocal)}</p>
              </div>
              {!validateAddressState?.error && (
                <div className="col-12 sm:col-6">
                  <p className="font-bold block mb-3">Validated Address:</p>
                  <p>{addressLabel(validateAddressState.validatedAddress)}</p>
                </div>
              )}
            </div>
          </div>
          {!validateAddressState?.error && (
            <div className="p-4 flex justify-content-end">
              <Button
                label="Approve Change"
                className="uppercase border-noround c-button-primary"
                onClick={handleApprove}
              />
            </div>
          )}
        </>
      </Dialog>
    </form>
  );
}
